import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  first,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import { User } from '../models/user';
import { ServiceBase } from './service-base';

@Injectable({
  providedIn: 'root',
})
export class UsersService extends ServiceBase implements OnDestroy {
  private currentUserSubject = new BehaviorSubject<User | null>(
    sessionStorage.getItem('user')
      ? JSON.parse(sessionStorage.getItem('user')!)
      : null
  );
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    super();
  }

  import(datas: any, properties?: any, rights?: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  ngOnDestroy(): void {
    throw new Error('Method not implemented.');
  }

  removeCurrentUser() {
    sessionStorage.clear();
  }

  public getSessionCurrentUser(): Observable<User | null> {
    return this.currentUserSubject.pipe(first());
  }

  // Gets the current user
  getCurrentUser(): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders(),
    };

    // Creates a new observable to handle the HTTP request
    return this.http.get<User>(`${this.apiUrl}/api/user`, httpOptions).pipe(
      tap((user: User) => {
        this.currentUserSubject.next(user);
      }),
      catchError((error) => {
        this.currentUserSubject.next(null);
        return throwError(error); // or any default value
      })
    );
  }

  getUsers(): Observable<User[]> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.getCurrentOrganizationId() ?? ''
      ),
    };

    return this.http.get<User[]>(`${this.apiUrl}/api/users`, httpOptions).pipe(
      catchError((error) => {
        console.error(
          'Erreur lors de la récupération des utilisateurs :',
          error
        );
        return throwError(() => error);
      })
    );
  }

  // Checks the current user with an access token
  checkCurrentUser(accessToken: string): Observable<User | undefined> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Authorization', accessToken)
        .set('X-OrganizationId', this.getCurrentOrganizationId() ?? ''),
    };

    return this.http.get<User>(`${this.apiUrl}/api/user`, httpOptions).pipe(
      tap((user: User) => {
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('SessionId', crypto.randomUUID());
        this.currentUserSubject.next(user);
      }),
      catchError((error) => {
        console.error('Error validating current user:', error);
        this.currentUserSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  private getCurrentOrganizationId(): string | null {
    const user = sessionStorage.getItem('user');
    return user ? JSON.parse(user).organizationId : null;
  }

  getOrganizationId(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/api/organizationId`).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getUserRights(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/api/user/rights`).pipe(
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  hasRight(requiredRight: string): Observable<boolean> {
    return this.getUserRights().pipe(
      map((rights) => rights.includes(requiredRight)),
      catchError((error) => {
        return of(false);
      })
    );
  }

  getOrganizationById(id: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders(),
    };
    return this.http
      .get<any>(`${this.apiUrl}/api/getOrga/${id}`, httpOptions)
      .pipe(
        tap((organization: any) => {}),
        catchError((error) => {
          console.error('Error fetching organization details:', error);
          return throwError(error);
        })
      );
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }
}
